import moment from "moment";

import { CalendarAppointmentDisplay } from "@/Appointments/Models/Calendar/CalendarAppointmentDisplay";
import { ServiceDefinitionWithItsRelatedCombos } from "@/Appointments/Models/Scheduling/ComboServiceModel";
import { PredefinedRuleSetModel } from "@/Appointments/Models/Scheduling/PredefinedRuleSetModel";
import { ICalendarModel } from "@/Appointments/Models/UI/ICalendarModel";
import { CalendarStore } from "@/Appointments/Stores/CalendarStore";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Guid } from "@/Common/Models/Guid";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";

class CalendarHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper;

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Misc.RedirectToTab, this.HandleScheduleRedirect);
        this.RegisterOperation(LocalOperations.Calendar.CalendarView_Day_Click, this.CalendarViewDayClick, false);
        this.RegisterOperation(LocalOperations.Calendar.CalendarView_Week_Click, this.CalendarViewWeekClick, false);
        this.RegisterOperation(LocalOperations.Calendar.CalendarView_Month_Click, this.CalendarViewMonthClick, false);
        this.RegisterOperation(LocalOperations.Calendar.CalendarPage_Next_Click, this.CalendarPageNextClick);
        this.RegisterOperation(LocalOperations.Calendar.CalendarPage_Previous_Click, this.CalendarPagePreviousClick);
        this.RegisterOperation(LocalOperations.Calendar.CalendarPage_PreviousDate_Click, this.CalendarPagePreviousDateClick);
        this.RegisterOperation(LocalOperations.Calendar.CalendarPage_NextDate_Click, this.CalendarPageNextDateClick);
        this.RegisterOperation(LocalOperations.Calendar.Calendar_Refresh_Click, this.RefreshCalendarClick, false);
        this.RegisterOperation(LocalOperations.Calendar.Calendar_Settings_Click, this.DisplayCalendarSettings);
        this.RegisterOperation(LocalOperations.Calendar.Calendar_PA_Click, this.DisplayPA);
        this.RegisterOperation(LocalOperations.Calendar.Calendar_MD_Click, this.DisplayMD);
        this.RegisterOperation(LocalOperations.Calendar.Calendar_Print_Click, this.DisplayPrintMenu);
        this.RegisterOperation(LocalOperations.Calendar.Calendar_Monthview_DayClick, this.CalendarNavigateToDay);
        this.RegisterOperation(LocalOperations.Calendar.Appointment_Show_Edit_Dialog, this.AppointmentShowEditDialog, false);
        this.RegisterOperation(LocalOperations.Calendar.Appointment_Show_Ruleset_Edit, this.RulesetShowEditDialog, false);
        this.RegisterOperation(LocalOperations.Calendar.Appointment_NewAppointment_Dialog, this.newAppointment);
        this.RegisterOperation(LocalOperations.Calendar.AppointmentEdit_DoubleBooking_Cancel, this.CancelDoubleBooking);
        this.RegisterOperation(LocalOperations.Calendar.AppointmentEdit_DoubleBooking_Continue, this.ConfirmDoubleBooking);
        this.RegisterOperation(LocalOperations.Calendar.Appointment_RemoveAppointment, this.RemoveAppointment);
        this.RegisterOperation(LocalOperations.Calendar.Appointment_RemovePredefinedAppointment, this.RemovePredefinedAppointment);
        this.RegisterOperation(LocalOperations.Calendar.OnScroll, this.OnScroll, false); // we don't emit changes on this one
        this.RegisterOperation(LocalOperations.Sidebar.DateChanged, this.SidebarDateChange);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_ClosePopup, this.closeRulesetPopup);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_SaveChanges, this.SaveRuleSet);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_OpenAddServicePopup, this.openAddServicePopup);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_StartOverBtnClick, this.handleStartOver);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_AddServicePopup_AddBtn_Click, this.handleAddService);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_CloseAddServicePopup, this.closeAddServicePopup);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_ChangeComboServiceSelection, this.changeComboServiceSelection);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_OpenRulesetCollisionPopup, this.openRulesetCollisionPopup);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_CloseRulesetCollisionPopup, this.closeRulesetCollisionPopup);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_StartDateChanged, this.changeRulesetStartDate);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_EndDateChanged, this.changeRulesetEndDate);
        this.RegisterOperation(LocalOperations.Calendar.Ruleset_ShowMinErrorPopup, this.showMinErrorPopup);
        this.RegisterOperation(LocalOperations.Calendar.Calendar_OnCloseAlreadyBookedAppointment, this.onCloseAlreadyBookedAppointment);
        this.RegisterOperation(LocalOperations.Calendar.Calendar_DeleteMyleRuleset, this.deleteMyleRuleset);
        this.RegisterOperation(LocalOperations.Calendar.RedirectToPatientFile, this.redirectToPatientFile);
        this.RegisterOperation(LocalOperations.Calendar.ShowQuestionnaire, this.showQuestionnaire);
        this.RegisterOperation(LocalOperations.Calendar.OnQuestionnaireSubmitted, this.handleQuestionnaireSubmitted);
        this.RegisterOperation(LocalOperations.Calendar.ShowNoticeBoard, this.showNoticeBoard);
    }

    private HandleScheduleRedirect(request: LocalEvent, store: CalendarStore): void {
        var target = request.Get<RedirectTo>("target");

        if (target === RedirectTo.Calendar) {
            var responsibleProviderId = request.Get<Guid>("value");
            store.patientToPreloadOnNextAppCreation = request.Get<Guid>("targetPatient");
            store.subject = request.Get<string>("subject");

            store.ShowCalendarTabForSpecificEmployee(responsibleProviderId);

            // ensure sidebar is reset to normal search mode
            sidebarStore.Model = sidebarStore.Model.set("IsAdvancedSearchView", false);
        }
    }

    private AppointmentShowEditDialog(request: LocalEvent, store: CalendarStore): void {
        var appointmentId: string = request.Get<string>("appointmentId");
        var isPredefinedAppointment: boolean = request.Get<boolean>("isPredefined");

        if (appointmentId) {
            // first we'll load the appointment details and we'll open the dialog when data is received
            store.calendarModel = store.calendarModel.set("SelectedAppointmentId", appointmentId);

            if (!isPredefinedAppointment) {
                store.calendarActions.LoadSelectedAppointment(store.calendarModel);
            }
            else {
                store.calendarActions.LoadSelectedPredefinedAppointment(store.calendarModel);
            }
        }

        store.isSaving = false;
        sessionStore.showGlobalOverlay(true);
    }

    private RulesetShowEditDialog(request: LocalEvent, store: CalendarStore): void {
        var rulesetId = request.Get<string>("rulesetId");

        if (rulesetId) {
            store.calendarActions.LoadRuleset(rulesetId);
        }
    }

    private newAppointment(request: LocalEvent, store: CalendarStore): void {
        if (cacheStore.ClinicSettings.IsCalendarReadOnly)
            return;

        const employeeContactFacetId = request.Get<Guid>("employeeContactFacetId");
        var startTime: Date = request.Get<Date>("startTime");

        store.SetNewAppointment(employeeContactFacetId, startTime);
        store.isSaving = false;

        sessionStore.showGlobalOverlay(true);
    }

    private RefreshCalendarClick(request: LocalEvent, store: CalendarStore): void {
        this.ClearCurrentCalendarPage(store);

        store.calendarActions.LoadCalendarAppointmentCounts(
            store.calendarModel,
            store.calendarDisplaySettingsModel,
            () => store.calendarActions.LoadCalendar(store.calendarModel, store.calendarDisplaySettingsModel),
            true
        );
    }

    private DisplayPA(): void {
        window.open(rootPath + "/palist", "medfar_palist");
    }
    private DisplayMD(request: LocalEvent, store: CalendarStore): void {
        store.calendarActions.GetWorkingClinicians(store);
    }

    private DisplayPrintMenu(request: LocalEvent, store: CalendarStore): void {
        store.calendarModel = store.calendarModel.set("IsPrintCalendarDialogVisible", true);
    }

    private DisplayCalendarSettings(request: LocalEvent, store: CalendarStore): void {
        var open = store.calendarModel.get("ShowCalendarDisplaySettings");
        store.calendarModel = store.calendarModel.set("ShowCalendarDisplaySettings", !open);

        // set original values if undo is needed
        store.undoCalendarDisplaySettings = store.calendarDisplaySettingsModel;
        store.undoSelectedEmployeesIds = this.helper.GetCheckedItemsFromICheckableList(store.calendarDisplaySettingsModel.get("EmployeesFiltered")).toArray().map(x => x.Id);
        store.undoSelectedRoomIds = this.helper.GetCheckedItemsFromICheckableList(store.calendarDisplaySettingsModel.get("RoomsFiltered")).toArray().map(x => x.Id);
        store.undoSelectedSiteIds = this.helper.GetCheckedItemsFromICheckableList(store.calendarDisplaySettingsModel.get("Sites")).toArray().map(x => x.Id);
    }

    private CalendarPagePreviousClick(request: LocalEvent, store: CalendarStore): void {
        store.SetCalendarEmployeesPreviousPage();
        this.calendarPageChange(store);
    }

    private CalendarPageNextClick(request: LocalEvent, store: CalendarStore): void {
        store.SetCalendarEmployeesNextPage();
        this.calendarPageChange(store);
    }

    private calendarPageChange(store: CalendarStore): void {
        if (!store.calendarModel.get("IsCalendarMonthView")) {
            this.ClearCurrentCalendarPage(store);
            store.calendarActions.LoadCalendar(store.calendarModel, store.calendarDisplaySettingsModel, SubServerOperations.Calendar.SearchAppointments_GeneralReload);
        }
        else if (!cacheStore.ClinicSettings.EnableDynamicRosterCalendarSorting) {
            this.ClearCurrentCalendarPage(store);
            store.calendarActions.LoadCalendarAppointmentCounts(store.calendarModel, store.calendarDisplaySettingsModel);
        }
    }

    // clear appointment displays to not previous appointments remain while loading new results
    private ClearCurrentCalendarPage(store: CalendarStore): void {
        const isMonthView = store.calendarModel.get("IsCalendarMonthView");

        store.calendarModel = store.calendarModel.set("AppointmentsDisplay", Immutable.List<CalendarAppointmentDisplay>([]));
        store.calendarModel = store.calendarModel.set("PredefinedAppointmentsDisplay", Immutable.List<CalendarAppointmentDisplay>([]));

        if (isMonthView)
            store.SetAllEmployeesAppointmentCounts([]);
        else
            store.SetAppointmentNumbersPerEmployee();
    }

    private CalendarPagePreviousDateClick(request: LocalEvent, store: CalendarStore): void {
        const PagesToJumpOnClick = -1;
        store.calendarModel = this.calendarChangePage(PagesToJumpOnClick, store.calendarModel);
        this.ClearCurrentCalendarPage(store);

        calendarStore.calendarActions.LoadCalendarAppointmentCounts(
            calendarStore.calendarModel,
            calendarStore.calendarDisplaySettingsModel,
            () => calendarStore.calendarActions.LoadCalendar(calendarStore.calendarModel, calendarStore.calendarDisplaySettingsModel)
        );
    }

    private CalendarPageNextDateClick(request: LocalEvent, store: CalendarStore): void {
        const PagesToJumpOnClick = 1;
        store.calendarModel = this.calendarChangePage(PagesToJumpOnClick, store.calendarModel);
        this.ClearCurrentCalendarPage(store);

        calendarStore.calendarActions.LoadCalendarAppointmentCounts(
            calendarStore.calendarModel,
            calendarStore.calendarDisplaySettingsModel,
            () => calendarStore.calendarActions.LoadCalendar(calendarStore.calendarModel, calendarStore.calendarDisplaySettingsModel)
        );
    }

    private calendarChangePage(PagesToJump: number, model: ICalendarModel): ICalendarModel {
        let dateToRememberDayView: Date = model.get("DateToRememberDayView");

        let currentStartDate = model.get("CalendarStartDate");
        let currentEndDate = model.get("CalendarEndDate");

        const isDayView = model.get("IsCalendarDayView");
        const isWeekView = model.get("IsCalendarWeekView");
        const isMonthView = model.get("IsCalendarMonthView");

        if (isDayView) {
            currentStartDate = moment(currentStartDate).add(PagesToJump, "day").startOf("day").toDate();
            currentEndDate = moment(currentStartDate).add(1, "day").startOf("day").toDate();
            dateToRememberDayView = currentStartDate;
            sessionStore.dispatch(LocalOperations.Sidebar.Calendar_SelectDate, { value: currentStartDate }, 0);
        }
        else if (isWeekView) {
            dateToRememberDayView = moment(dateToRememberDayView).add(PagesToJump, "week").toDate();
            currentStartDate = moment(currentStartDate).add(1, "day").toDate();
            currentEndDate = moment(currentEndDate).add(1, "day").toDate();
            currentStartDate = moment(currentStartDate).startOf("isoWeek").add(PagesToJump, "week").toDate();
            currentEndDate = moment(currentStartDate).endOf("isoWeek").toDate();
            currentStartDate = moment(currentStartDate).add(-1, "day").toDate();
            currentEndDate = moment(currentEndDate).add(-1, "day").toDate();
        }
        else if (isMonthView) {
            currentStartDate = moment(currentStartDate).add(PagesToJump, "month").startOf("month").toDate();
            currentEndDate = moment(currentStartDate).endOf("month").toDate();

            let dateToSet = dateToRememberDayView.getDate();
            const lastDateOfMonth = moment(currentStartDate).endOf("month").toDate().getDate();

            if (dateToSet > lastDateOfMonth)
                dateToSet = lastDateOfMonth;
            dateToRememberDayView = new Date(currentStartDate.getFullYear(), currentStartDate.getMonth(), dateToSet);
        }

        model = model.set("DateToRememberDayView", dateToRememberDayView);
        model = model.set("CalendarStartDate", currentStartDate);
        model = model.set("CalendarEndDate", currentEndDate);

        return model;
    }

    private CalendarViewDayClick(request: LocalEvent, store: CalendarStore): void {
        const model = store.calendarModel;
        const startDate = new Date();
        const currentEndDate = moment(startDate).add(1, "day").startOf("day").toDate();

        store.calendarModel = model.set("IsCalendarDayView", true)
            .set("IsCalendarWeekView", false)
            .set("IsCalendarMonthView", false)
            .set("CalendarStartDate", startDate)
            .set("CalendarEndDate", currentEndDate);

        sessionStore.dispatch(LocalOperations.Sidebar.Calendar_SelectDate, { value: startDate }, 0);

        this.ClearCurrentCalendarPage(store);
        store.SetCalendarEmployees();
    }

    public CalendarViewWeekClick(request: LocalEvent, store: CalendarStore): void {
        const model = store.calendarModel;
        const currentStartDate = model.get("CalendarStartDate");

        store.calendarModel = model.set("IsCalendarDayView", false)
            .set("IsCalendarWeekView", true)
            .set("IsCalendarMonthView", false)
            .set("CalendarStartDate", moment(currentStartDate).startOf("week").toDate())
            .set("CalendarEndDate", moment(currentStartDate).endOf("week").toDate());

        this.ClearCurrentCalendarPage(store);
        store.SetCalendarEmployees();
    }

    public CalendarViewMonthClick(request: LocalEvent, store: CalendarStore): void {
        const model = store.calendarModel;

        store.calendarModel = model.set("IsCalendarDayView", false)
            .set("IsCalendarWeekView", false)
            .set("IsCalendarMonthView", true)
            .set("CalendarStartDate", moment(model.get("CalendarEndDate")).startOf("month").toDate())
            .set("CalendarEndDate", moment(model.get("CalendarEndDate")).endOf("month").toDate());

        this.ClearCurrentCalendarPage(store);
        store.SetCalendarEmployees();
    }

    /** -------------------------------------------------------------------------------------- **/

    private RemoveAppointment(request: LocalEvent, store: CalendarStore): void {
        var appointmentId = request.Get<string>("appointmentId");

        if (store.calendarModel.get("Appointments")) {
            var appts = store.calendarModel.get("Appointments").toArray().filter(x => x.AppointmentId == appointmentId);
            if (appts && appts.length > 0) {
                var appt = appts[0];

                store.calendarActions.CancelAppointment(appt);

                // remove from model
                var apptsToKeep = store.calendarModel.get("Appointments").toArray().filter(x => x.AppointmentId != appointmentId);
                store.calendarModel = store.calendarModel.set("Appointments", Immutable.List(apptsToKeep));
            }
        }
    }

    private RemovePredefinedAppointment(request: LocalEvent, store: CalendarStore): void {
        var appointmentId = request.Get<string>("predefinedAppointmentId");
        store.calendarActions.DeletePredefinedAppointment(appointmentId);
    }

    private ConfirmDoubleBooking(request: LocalEvent, store: CalendarStore): void {
        store.calendarActions.SaveAppointment(store);
        store.calendarModel = store.calendarModel.set("IsDoubleBookingDialogVisible", false);
        sessionStore.hideGlobalOverlay();
    }

    private CancelDoubleBooking(request: LocalEvent, store: CalendarStore): void {
        store.calendarModel = store.calendarModel.set("IsDoubleBookingDialogVisible", false);
        sessionStore.hideGlobalOverlay();
    }

    private CalendarNavigateToDay(request: LocalEvent, store: CalendarStore): void {
        const date = request.Get<Date>("date");
        const currentStartDate = moment(date).startOf("day").toDate();
        const currentEndDate = moment(date).add(1, "day").startOf("day").toDate();

        store.calendarModel = store.calendarModel.set("IsCalendarDayView", true);
        store.calendarModel = store.calendarModel.set("IsCalendarWeekView", false);
        store.calendarModel = store.calendarModel.set("IsCalendarMonthView", false);
        store.calendarModel = store.calendarModel.set("CalendarStartDate", currentStartDate);
        store.calendarModel = store.calendarModel.set("CalendarEndDate", currentEndDate);

        this.ClearCurrentCalendarPage(store);

        store.calendarActions.LoadCalendarAppointmentCounts(
            store.calendarModel,
            store.calendarDisplaySettingsModel,
            () => store.calendarActions.LoadCalendar(store.calendarModel, store.calendarDisplaySettingsModel)
        );
    }

    private OnScroll(request: LocalEvent, store: CalendarStore): void {
        var position = request.Get<number>("value");
        if (position == null) position = 0;
        if (position < 0) position = 0;
        store.calendarModel = store.calendarModel.set("ScrollPosition", position);
    }

    private SidebarDateChange(request: LocalEvent, store: CalendarStore): void {
        const newDate = moment(request.Get<Date>("value")).startOf("day").toDate();
        const newDateEnd = moment(newDate).add(1, "day").startOf("day").toDate();

        store.calendarModel = store.calendarModel.set("CalendarStartDate", newDate)
            .set("CalendarEndDate", newDateEnd)
            .set("IsCalendarDayView", true)
            .set("IsCalendarWeekView", false)
            .set("IsCalendarMonthView", false);

        store.calendarActions.LoadCalendarAppointmentCounts(
            store.calendarModel,
            store.calendarDisplaySettingsModel,
            () => store.calendarActions.LoadCalendar(store.calendarModel, store.calendarDisplaySettingsModel)
        );
    }

    private closeRulesetPopup(request: LocalEvent, store: CalendarStore): void {
        this.closeAddServicePopup(request, store);

        store.calendarRulesetModel = store.calendarRulesetModel.set("IsRulesetPopupOpen", false);
        store.calendarRulesetModel = store.calendarRulesetModel.set("RulesetForEdit", [].toImmutable());
        store.calendarRulesetModel = store.calendarRulesetModel.set("SelectedRuleset", null);
        sessionStore.hideGlobalOverlay();
    }

    private SaveRuleSet(request: LocalEvent, store: CalendarStore): void {
        let modifiedServices = request.Get<ServiceDefinitionWithItsRelatedCombos[]>("modifiedServices");
        let originalRuleset = request.Get<PredefinedRuleSetModel>("originalRuleset");
        let siteIds = request.Get<string[]>("siteIds");
        let forceDelete = request.Get<boolean>("forceDelete");
        let startDate = request.Get<Date>("startDate");
        let endDate = request.Get<Date>("endDate");
        let isBookingHubAvailability = request.Get<boolean>("isBookingHubAvailability");

        // originalRuleset.Model.EmployeeId
        store.calendarActions.CheckIfRulesetIsValid(modifiedServices, originalRuleset.Model.ID, isValid => {
            if (!isValid)
                store.calendarRulesetModel = store.calendarRulesetModel.set("HasRulesetModificationFailed", true);
            else {
                store.calendarActions.UpdateRuleset(
                    modifiedServices,
                    originalRuleset.Model.ID,
                    originalRuleset.Model.EmployeeId,
                    siteIds,
                    startDate,
                    endDate,
                    forceDelete,
                    isBookingHubAvailability
                );
            }
        });
    }

    private openAddServicePopup(request: LocalEvent, store: CalendarStore): void {
        store.calendarRulesetModel = store.calendarRulesetModel.set("IsAddServicePopupOpen", true);

        let selectedComboList = store.calendarRulesetModel.get("RulesetForEdit");
        store.calendarRulesetModel = store.calendarRulesetModel.set("SelectedComboServiceList", selectedComboList);
    }

    private closeAddServicePopup(request: LocalEvent, store: CalendarStore): void {
        store.calendarRulesetModel = store.calendarRulesetModel.set("IsAddServicePopupOpen", false);
        store.calendarRulesetModel = store.calendarRulesetModel.set("SelectedComboServiceList", [].toImmutable());
    }

    private handleStartOver(request: LocalEvent, store: CalendarStore): void {
        store.calendarRulesetModel = store.calendarRulesetModel.set("IsAddServicePopupOpen", true);

        store.calendarRulesetModel = store.calendarRulesetModel.set("RulesetForEdit", [].toImmutable());
        store.calendarRulesetModel = store.calendarRulesetModel.set("SelectedComboServiceList", [].toImmutable());
    }

    private handleAddService(request: LocalEvent, store: CalendarStore): void {
        let selectedServiceList = store.calendarRulesetModel.get("SelectedComboServiceList");
        store.calendarRulesetModel = store.calendarRulesetModel.set("RulesetForEdit", selectedServiceList);

        this.closeAddServicePopup(request, store);
    }

    private changeComboServiceSelection(request: LocalEvent, store: CalendarStore): void {
        var selected = request.Get<Immutable.List<ServiceDefinitionWithItsRelatedCombos>>("selectedItems");
        if (selected == null)
            selected = [].toImmutable();

        store.calendarRulesetModel = store.calendarRulesetModel.set("SelectedComboServiceList", selected);
    }

    private openRulesetCollisionPopup(request: LocalEvent, store: CalendarStore): void {
        let existingRulesetDates = request.Get<Date[]>("existingRulesetDates");
        store.calendarRulesetModel = store.calendarRulesetModel.set("ExistingRuleDate", existingRulesetDates);
        store.calendarRulesetModel = store.calendarRulesetModel.set("IsRulesetCollisionPopupOpen", true);
    }

    private closeRulesetCollisionPopup(request: LocalEvent, store: CalendarStore): void {
        let closeRulesetPopup = request.Get<boolean>("closeRulesetPopup");
        store.calendarRulesetModel = store.calendarRulesetModel.set("IsRulesetCollisionPopupOpen", false);

        if (closeRulesetPopup) {
            sessionStore.hideGlobalOverlay();
            this.closeRulesetPopup(request, store);
            sessionStore.dispatch(LocalOperations.Calendar.Calendar_Refresh_Click, null, 50);
        }
    }

    private changeRulesetStartDate(request: LocalEvent, store: CalendarStore): void {
        let startDate = request.Get<Date>("startDate");
        store.calendarRulesetModel = store.calendarRulesetModel.set("StartDate", startDate);
    }

    private changeRulesetEndDate(request: LocalEvent, store: CalendarStore): void {
        let endDate = request.Get<Date>("endDate");
        store.calendarRulesetModel = store.calendarRulesetModel.set("EndDate", endDate);
    }

    private showMinErrorPopup(request: LocalEvent, store: CalendarStore): void {
        let hide = request.Get<boolean>("hide");
        if (hide) {
            store.calendarRulesetModel = store.calendarRulesetModel.set("MinErrorPopupOpen", false);
        }
        else {
            store.calendarRulesetModel = store.calendarRulesetModel.set("MinErrorPopupOpen", true);
        }
    }

    private onCloseAlreadyBookedAppointment(request: LocalEvent, store: CalendarStore): void {
        store.calendarModel = store.calendarModel.set("OpenAlreadyBookedPopup", false);

        store.calendarActions.LoadCalendarAppointmentCounts(
            store.calendarModel,
            store.calendarDisplaySettingsModel,
            () => store.calendarActions.LoadCalendar(store.calendarModel, store.calendarDisplaySettingsModel)
        );

        sessionStore.hideGlobalOverlay();
    }

    private deleteMyleRuleset(request: LocalEvent): void {
        let rulesetId = request.Get<number>("rulesetId");
        if (rulesetId != null && rulesetId > 0) {
            calendarStore.calendarActions.deleteMyleRuleset(rulesetId);
        }
    }

    private redirectToPatientFile(request: LocalEvent): void {
        let patientContactId = request.Get<Guid>("patientContactId");
        patientStore.setActivePatientUI(patientContactId);
    }

    private showQuestionnaire(request: LocalEvent, store: CalendarStore): void {
        let showQuestionnaire = request.Get<boolean>("showQuestionnaire");
        let questionnaireStatusInfo = request.Get<QuestionnaireStatusInfo>("questionnaireStatusInfo");

        store.calendarModel = store.calendarModel
            .set("SelectedQuestionniare", showQuestionnaire ? new QuestionnaireStatusInfo(questionnaireStatusInfo) : null);
    }

    private handleQuestionnaireSubmitted(request: LocalEvent, store: CalendarStore): void {
        let result = request.Get<boolean>("result");
        let selectedQuestionnaire = store.calendarModel.get("SelectedQuestionniare");

        selectedQuestionnaire.Result = result;
        store.calendarModel = store.calendarModel
            .set("SelectedQuestionniare", null);

        let appointments = store.calendarModel.get("AppointmentsDisplay").toArray();
        appointments.forEach(x => {
            if (x.AppointmentId === selectedQuestionnaire.AppointmentId)
                x.QuestionnaireStatusInfo = selectedQuestionnaire;
        });
        store.calendarModel = store.calendarModel.set("AppointmentsDisplay", Immutable.List(appointments));
    }

    private showNoticeBoard(request: LocalEvent, store: CalendarStore): void {
        store.isNoticeBoard = request.Get<boolean>("showNoticeBoard");

        if (!store.isNoticeBoard) {
            store.calendarActions.LoadCalendarAppointmentCounts(
                store.calendarModel,
                store.calendarDisplaySettingsModel,
                () => store.calendarActions.LoadCalendar(store.calendarModel, store.calendarDisplaySettingsModel)
            );
        }
    }
}

export { CalendarHandler };
